import React from 'react';
import { Box, Card, CardContent, Paper, Grid, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { LayoutInfo } from '../../../state/Layouts';
import { LayoutMetadataChange, LocalizedField } from '../../../API';
import { getBusinessFriendlyFieldName } from './utils';
import { useTimezones } from '../../../hooks';

type LayoutComparisonSummaryProps = {
  summary?: string;
  sourceLayout: LayoutInfo;
  targetLayout: LayoutInfo;
  metadataChanges: LayoutMetadataChange[];
};

const useStyles = makeStyles()((theme) => ({
  summaryCard: {
    marginBottom: theme.spacing(4),
    background: theme.palette.background.paper
  },
  jsonValue: {
    fontFamily: 'monospace',
    fontSize: '0.65rem',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
    margin: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    whiteSpace: 'pre-wrap'
  },
  arrayValue: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    paddingLeft: theme.spacing(2)
  },
  arrayValueAddedItem: {
    color: theme.palette.success.main
  },
  arrayValueRemovedItem: {
    color: theme.palette.error.main
  }
}));

export function LayoutComparisonSummary({
  summary,
  sourceLayout,
  targetLayout,
  metadataChanges
}: LayoutComparisonSummaryProps): React.ReactElement {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { timestamp } = useTimezones();

  const getDisplayValue = (
    value: string | LocalizedField | null | undefined,
    oldValue: string | LocalizedField | null | undefined = null
  ) => {
    if (value === null || value === undefined) return t('general.none');
    if (typeof value === 'string') {
      if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)) {
        return timestamp(value);
      }
      return value;
    }
    if (Array.isArray(value)) {
      let valuesRemoved = [];
      let valuesAdded = [];
      if (oldValue && Array.isArray(oldValue)) {
        valuesRemoved = oldValue.filter((v) => v !== null && v !== undefined && !value.includes(v));
        valuesAdded = value.filter((v) => v !== null && v !== undefined && !oldValue.includes(v));
      }
      return (
        <>
          <div className={classes.arrayValue}>
            {valuesAdded.length > 0 && (
              <div className={classes.arrayValueAddedItem}>
                Added: {valuesAdded.map((v) => getDisplayValue(v)).join(', ')}
              </div>
            )}
            {valuesRemoved.length > 0 && (
              <div className={classes.arrayValueRemovedItem}>
                Removed: {valuesRemoved.map((v) => getDisplayValue(v)).join(', ')}
              </div>
            )}
          </div>
          <pre className={classes.jsonValue}>{JSON.stringify(value, null, 2)}</pre>
        </>
      );
    }
    if (typeof value === 'object' && value) {
      return <pre className={classes.jsonValue}>{JSON.stringify(value, null, 2)}</pre>;
    }
    return String(value);
  };

  return (
    <Card className={classes.summaryCard}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('layouts.comparison_summary')}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {summary || t('layouts.no_summary_available')}
        </Typography>

        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} style={{ padding: 16 }}>
                <Typography variant="subtitle1" color="secondary">
                  {t('layouts.source_layout')}
                </Typography>
                <Typography variant="body2">
                  {t('layouts.modules_count')}: <b>{sourceLayout?.uiModules?.length || 0}</b>
                </Typography>

                {/* Source Layout Metadata */}
                {metadataChanges && metadataChanges.length > 0 && (
                  <>
                    {metadataChanges.map((change: LayoutMetadataChange, index: number) => (
                      <Typography key={index} variant="body2">
                        {getBusinessFriendlyFieldName(change.fieldName)}: <b>{getDisplayValue(change.oldValue)}</b>
                      </Typography>
                    ))}
                  </>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} style={{ padding: 16 }}>
                <Typography variant="subtitle1" color="secondary">
                  {t('layouts.target_layout')}
                </Typography>
                <Typography variant="body2">
                  {t('layouts.modules_count')}: <b>{targetLayout?.uiModules?.length || 0}</b>
                </Typography>

                {/* Target Layout Metadata */}
                {metadataChanges && metadataChanges.length > 0 && (
                  <>
                    {metadataChanges.map((change: LayoutMetadataChange, index: number) => (
                      <Typography key={index} variant="body2">
                        {getBusinessFriendlyFieldName(change.fieldName)}:{' '}
                        <b>{getDisplayValue(change.newValue, change.oldValue)}</b>
                      </Typography>
                    ))}
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Remove the separate metadata changes section */}
      </CardContent>
    </Card>
  );
}
